import { Card, HorizontalScrollView } from "@clipboard-health/ui-components";
import { CardContent, Skeleton, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useIsWorkplaceReviewUXUpdateEnabled } from "@src/appV2/PostShiftReview/hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { Button } from "@src/appV2/redesign/components/Button";
import { PLACEMENT_WORKPLACE_REVIEWS_PATH } from "@src/appV2/redesign/Placements/paths";
import { useWorkplaceModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { useHideReviewsUIByFacilityId } from "@src/appV2/redesign/Workplace/hooks/useHideReviewsUIByFacilityId";
import {
  useWorkplaceComments,
  WORKPLACE_COMMENTS_PAGE_SIZE,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { WorkplaceReviewCommentCompactCardContainer } from "@src/appV2/redesign/Workplace/Review/Comment/CompactCardContainer";
import { useRef } from "react";

interface WorkplaceReviewsSectionProps {
  workplaceId: string;
  placementId: string;
}

const MAX_VISIBLE_COMMENTS = 2;

export function WorkplaceReviewsSection(props: WorkplaceReviewsSectionProps) {
  const { workplaceId, placementId } = props;

  const { generateModalPath } = useWorkplaceModalsDataContext();
  const { isEnabled: isReviewUXUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();
  const { facilityIds: facilityIdsToHideReviews } = useHideReviewsUIByFacilityId();
  const hasScrolledRef = useRef<boolean>(false);

  const shouldShowReviews = !facilityIdsToHideReviews.includes(workplaceId);

  const handleReviewsScroll = () => {
    if (hasScrolledRef.current) {
      return;
    }

    hasScrolledRef.current = true;
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
      action: "workplace_reviews_scrolled",
      source: "placement_details_page",
      placementId,
      workplaceId,
    });
  };

  const {
    data: comments = [],
    isLoading: commentsIsLoading,
    isSuccess,
    isError,
  } = useWorkplaceComments(
    {
      workplaceId,
      page: { size: WORKPLACE_COMMENTS_PAGE_SIZE },
      filter: isReviewUXUpdateEnabled ? { sort: "-relevancyScore" } : undefined,
    },
    { enabled: shouldShowReviews }
  );

  if (!shouldShowReviews) {
    return null;
  }

  if (commentsIsLoading) {
    return <WorkplaceReviewsSectionSkeleton />;
  }

  if (isError || !isSuccess || comments.length === 0) {
    return null;
  }

  const displayedComments = comments.slice(0, MAX_VISIBLE_COMMENTS);

  return (
    <Card>
      <CardContent>
        <HorizontalScrollView
          stretch
          outerPaddingCompensation={7}
          data-testid="workplace-reviews-scroll-container"
          onScroll={handleReviewsScroll}
        >
          {displayedComments.map((comment) => (
            <WorkplaceReviewCommentCompactCardContainer
              key={comment.id}
              isReadOnly
              comment={comment}
              workplaceId={workplaceId}
              showEngagementActions={false}
              onLikeChanged={() => {
                // Reviews are read-only in placement details.
              }}
            />
          ))}
        </HorizontalScrollView>

        <Button
          fullWidth
          variant="outlined"
          size="medium"
          sx={{ marginTop: 8 }}
          href={generateModalPath(PLACEMENT_WORKPLACE_REVIEWS_PATH, {
            workplaceId,
          })}
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_SCREEN_OPENED, {
              workplaceId,
              trigger: "Placement Details Page",
              placementId,
            });
          }}
        >
          {comments.length === 1
            ? "View 1 review"
            : `View all ${comments.length >= WORKPLACE_COMMENTS_PAGE_SIZE ? `${WORKPLACE_COMMENTS_PAGE_SIZE}+` : comments.length} reviews`}
        </Button>
      </CardContent>
    </Card>
  );
}

function WorkplaceReviewsSectionSkeleton() {
  return (
    <Card data-testid="workplace-reviews-section-skeleton">
      <CardContent>
        <WorkplaceReviewsLoadingContent />
      </CardContent>
    </Card>
  );
}

function WorkplaceReviewsLoadingContent() {
  return (
    <>
      <HorizontalScrollView stretch outerPaddingCompensation={7}>
        {[0, 1].map((index) => (
          <Card key={index} outlined variant="tertiary">
            <CardContent sx={{ padding: 7, minHeight: "11rem" }}>
              <Stack spacing={3} data-testid={`workplace-review-loading-card-${index}`}>
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="45%" />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </HorizontalScrollView>

      <Button fullWidth disabled variant="outlined" size="medium" sx={{ marginTop: 8 }}>
        Loading reviews...
      </Button>
    </>
  );
}
